/**
 * analytics — the single entry point for Google Analytics 4.
 *
 * Nothing else in the app touches `window.gtag` directly, so analytics can be
 * disabled, swapped, or made a no-op from this one file.
 *
 * Gating is by *hostname* rather than an environment variable, deliberately:
 * Create React App only inlines variables prefixed `REACT_APP_` (anything else
 * compiles to `undefined` in the browser with no error), and Vercel builds
 * preview deployments with NODE_ENV=production — so neither a plain env var nor
 * a NODE_ENV check can keep preview traffic out of the production property.
 * The hostname always can.
 *
 * The Measurement ID is not a secret. It ships in the bundle and is readable
 * from View Source on every site that runs GA, so there is nothing to protect
 * by moving it into config.
 */

const MEASUREMENT_ID = "G-68FML96W64";

// Only these hosts report. localhost and journim-front-end-*.vercel.app previews
// stay silent, so development and preview traffic never inflates real numbers.
const ENABLED_HOSTS = new Set(["journim.com", "www.journim.com"]);

let initialized = false;

function enabled() {
  try {
    return ENABLED_HOSTS.has(window.location.hostname);
  } catch {
    // No window (SSR, test renderer) — never report.
    return false;
  }
}

// Must push the `arguments` object itself, not a rest array: gtag.js reads
// Arguments and silently ignores a plain array, which fails with no error.
function gtag() {
  window.dataLayer.push(arguments);
}

/**
 * Load and configure gtag. Safe to call more than once. A no-op off production.
 * Call once, as early as possible, before React renders.
 */
export function initAnalytics() {
  if (initialized || !enabled()) return;
  initialized = true;

  try {
    window.dataLayer = window.dataLayer || [];
    window.gtag = gtag;

    gtag("js", new Date());

    // send_page_view:false because App.js fires page_view itself on every route
    // change. Left on, GA's own history-change detection would double-count each
    // navigation in this SPA — and it fires before the lazy route chunk resolves,
    // so its page_title would be wrong as well.
    gtag("config", MEASUREMENT_ID, { send_page_view: false });

    // Queued commands above are drained by gtag.js once it loads, so appending
    // the script last costs nothing and keeps it off the critical path.
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);
  } catch {
    // Analytics must never break the app.
  }
}

/** Record a pageview. `path` should include the query string. */
export function trackPageView(path, title) {
  if (!window.gtag) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  } catch {
    /* best-effort */
  }
}

/** Record a custom event. `params` values must be primitives. */
export function trackEvent(name, params = {}) {
  if (!window.gtag) return;
  try {
    window.gtag("event", name, params);
  } catch {
    /* best-effort */
  }
}

/**
 * Tie subsequent events to a Clerk user id, so one person across mobile and
 * desktop is one user rather than two. Pass null on sign-out.
 *
 * Only ever pass the opaque Clerk id. Sending an email address, phone number, or
 * name to Google Analytics breaks their terms of service and is a privacy leak.
 */
export function setAnalyticsUser(userId) {
  if (!window.gtag) return;
  try {
    window.gtag("set", { user_id: userId || undefined });
  } catch {
    /* best-effort */
  }
}
